import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ID } from '@datorama/akita';
import { debounceTime, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { MarketData } from './marketdata.model';
import { MarketDataStore } from './marketdata.store';
import { MarketDataQuery } from './marketdata.query';
import { SbEventBusService } from '@services';
import { Subject } from 'rxjs';
import { AppConfigQuery } from '../../../core/state/app-config/app-config.query';

@Injectable({ providedIn: 'root' })
export class MarketDataService {
    unsubscribe$ = new Subject();
    constructor(
        private marketDataStore: MarketDataStore,
        private marketDataQuery: MarketDataQuery,
        private http: HttpClient,
        private config: AppConfigQuery,
        private sbEventBus: SbEventBusService
    ) {
        sbEventBus
            .getObservable('TransactionSubmitted')
            .pipe(
                filter((event) => event && event.payload),
                map((event) => event?.payload?.value[0]?.instrument?.instrumentId),
                filter((instrumentId) => instrumentId),
                debounceTime(3000),
                switchMap((instrumentId) => this.get(instrumentId)),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();
    }

    get(instrumentId) {
        return this.http
            .get<MarketData[]>(
                `${
                    this.config.get().gatewayBaseUrl
                }/marketdata/instrument-value?instrumentId=${instrumentId}`
            )
            .pipe(
                tap((entities) => {
                    // temporary solution for adding unique ids per entry in store
                    const fixedData = entities.map((entity, i) => ({
                        ...entity,
                        id: entity.date
                    }));
                    this.marketDataStore.set(fixedData);
                })
            );
    }

    add(marketData: MarketData, id?: ID) {
        return this.http
            .post<MarketData[]>(
                `${this.config.get().gatewayBaseUrl}/marketdata/instrument-value`,
                marketData
            )
            .pipe(
                tap((entity) => {
                    this.marketDataStore.upsert(id, {
                        ...entity,
                        id
                    });
                })
            );
    }

    update(id: ID, marketData: Partial<MarketData>) {
        this.marketDataStore.update(id, marketData);
    }

    remove(id: ID) {
        const marketData = this.marketDataQuery.getValue().entities[id];
        const modified = {
            date: marketData.date,
            instrumentId: marketData.instrumentId
        };
        return this.http
            .post<MarketData[]>(
                `${this.config.get().gatewayBaseUrl}/marketdata/instrument-value`,
                modified
            )
            .pipe(
                tap((entity) => {
                    this.marketDataStore.remove(id);
                })
            );
    }
}
